import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Check, Lightning, Copy, Tag, Clock, Trophy, ChartBar, ShieldCheck, Crosshair, Binoculars, UsersThree } from "@phosphor-icons/react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import PublicNav from "@/components/PublicNav";
import { PlayerPhoto } from "@/components/PlayerPhoto";

const STATS = [
  { value: "6,976", label: "Dynasty leagues analyzed" },
  { value: "167,265", label: "Real trades studied" },
  { value: "9,983", label: "Champion rosters decoded" },
];

const PRO_FEATURES = [
  { icon: Lightning, title: "AI Trade Packages", desc: "Offers built for YOUR roster with accept-probability — not generic value charts" },
  { icon: Binoculars, title: "Trade Finder + Target a Player", desc: "Auto-scan your league for mutual-fit deals, or get realistic offers to land anyone" },
  { icon: ChartBar, title: "Full Dynasty Values + Contender Rankings", desc: "Live market values and exactly where your team stands in the title race" },
  { icon: UsersThree, title: "Unlimited leagues, unlimited analyses", desc: "Every Sleeper league you're in. No monthly caps, no limits" },
];

const FAQS = [
  { q: "What happens after the first year?", a: "Your subscription renews at the regular $29/year. No surprises — we state it right on this page. Cancel before renewal and you won't be charged." },
  { q: "Can I cancel?", a: "Yes, whenever you want, right from your billing page. You keep Pro access until the end of your paid period." },
  { q: "Which leagues does this work with?", a: "Sleeper. Import any Sleeper dynasty league with just your username — no password needed." },
];

function Countdown({ expiresAt, compact, final: isFinal }) {
  const [left, setLeft] = useState(expiresAt * 1000 - Date.now());
  useEffect(() => {
    const t = setInterval(() => setLeft(expiresAt * 1000 - Date.now()), 1000);
    return () => clearInterval(t);
  }, [expiresAt]);
  if (left <= 0) return null;
  const d = Math.floor(left / 86400000);
  const h = Math.floor((left % 86400000) / 3600000);
  const m = Math.floor((left % 3600000) / 60000);
  const s = Math.floor((left % 60000) / 1000);
  if (compact) {
    return <span className="font-mono font-bold text-[#ff3b30]">{d > 0 ? `${d}d ` : ""}{h}h {m}m {s}s</span>;
  }
  const Unit = ({ v, l }) => (
    <div className={`px-3 sm:px-4 py-2.5 text-center min-w-[58px] border ${isFinal ? "bg-[#ff3b30]/10 border-[#ff3b30]/50" : "bg-[#171717] border-[#262626]"}`}>
      <p className={`font-heading text-2xl sm:text-3xl ${isFinal ? "text-[#ff3b30]" : "text-[#ccff00]"}`}>{String(v).padStart(2, "0")}</p>
      <p className="text-[9px] uppercase tracking-wider text-[#666]">{l}</p>
    </div>
  );
  return (
    <div className="flex items-center gap-2 sm:gap-3" data-testid="promo-countdown">
      {d > 0 && <Unit v={d} l="Days" />}<Unit v={h} l="Hours" /><Unit v={m} l="Mins" /><Unit v={s} l="Secs" />
    </div>
  );
}

function SampleVerdict() {
  return (
    <div className="bg-[#111]/95 border border-[#262626] p-4 backdrop-blur-sm" data-testid="promo-sample-verdict">
      <p className="text-[9px] font-bold uppercase tracking-wider text-[#ccff00] mb-3">PRO AI TRADE PACKAGE — SAMPLE</p>
      <div className="flex items-center gap-3 mb-3">
        <div className="flex items-center gap-2 flex-1">
          <PlayerPhoto playerId="9509" name="Bijan Robinson" position="RB" size={34} />
          <div>
            <p className="text-sm text-white font-bold">Bijan Robinson</p>
            <p className="text-[10px] text-[#888]">RB · ATL · <span className="text-[#ccff00]">10,198</span></p>
          </div>
        </div>
        <span className="text-[10px] text-[#555] font-bold px-1">FOR</span>
        <div className="flex-1 text-right">
          <div className="flex items-center gap-2 justify-end mb-1">
            <div>
              <p className="text-sm text-white font-bold">Breece Hall</p>
              <p className="text-[10px] text-[#888]">RB · NYJ · <span className="text-[#ccff00]">5,800</span></p>
            </div>
            <PlayerPhoto playerId="8155" name="Breece Hall" position="RB" size={30} />
          </div>
          <div className="flex items-center gap-2 justify-end">
            <p className="text-xs text-white">+ 2026 1st <span className="text-[#ccff00]">4,200</span></p>
            <div className="w-7 h-7 rounded-full bg-[#262626] flex items-center justify-center"><span className="text-[8px] font-mono text-[#888]">1st</span></div>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 px-3 py-2 bg-[#4ade80]/10 border border-[#4ade80]/30 rounded-sm">
        <span className="text-[11px] font-bold text-[#4ade80] uppercase">82% ACCEPT PROBABILITY</span>
        <span className="text-[10px] text-[#a3a3a3]">Fills their RB need, wins your rebuild</span>
      </div>
    </div>
  );
}

export default function PromoPage({ user }) {
  const [promo, setPromo] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [, forceTick] = useState(0);

  useEffect(() => {
    const t = setInterval(() => forceTick(v => v + 1), 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    axios.get(`${API}/billing/promo/dynasty50`)
      .then(res => setPromo(res.data))
      .catch(() => setError(true));
  }, []);

  const deadline = "Friday, October 30";

  const handleClaim = async (auto = false) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/billing/checkout`, {
        plan_id: "pro",
        promo_code: "DYNASTY50",
        origin_url: window.location.origin,
      });
      if (res.data.url) window.location.href = res.data.url;
    } catch (e) {
      if (e.response?.status === 401) {
        if (auto) {
          toast.error("Sign-in didn't complete — tap the button to try again");
        } else {
          toast.info("Sign in with Google first — your 50% off carries straight into checkout");
          setTimeout(() => {
            window.location.href = "/api/auth/google/login?next=" + encodeURIComponent("/dynasty50?claim=1");
          }, 1200);
        }
      } else {
        toast.error(e.response?.data?.detail || "Failed to start checkout");
      }
    } finally { setLoading(false); }
  };

  // Auto-resume checkout after returning from Google sign-in (?claim=1)
  const autoClaimed = useRef(false);
  useEffect(() => {
    if (autoClaimed.current || !promo?.active) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("claim") !== "1") return;
    let stored = null;
    try { stored = JSON.parse(localStorage.getItem("dynastydeal_user") || "null"); } catch { /* ignore */ }
    if (!stored?.email) return;
    autoClaimed.current = true;
    window.history.replaceState({}, "", "/dynasty50");
    toast.success("Signed in — taking you to checkout with 50% off applied");
    handleClaim(true);
  }, [promo]); // eslint-disable-line react-hooks/exhaustive-deps

  const copyCode = () => {
    navigator.clipboard.writeText("DYNASTY50");
    toast.success("Code DYNASTY50 copied");
  };

  const expired = promo && !promo.active;
  const isFinal = !expired && promo?.expires_at && (promo.expires_at * 1000 - Date.now()) < 24 * 3600 * 1000 && (promo.expires_at * 1000 - Date.now()) > 0;

  const ClaimButton = ({ testid, full }) => (
    <button
      onClick={() => handleClaim()}
      disabled={loading || error}
      className={`${full ? "w-full" : "w-full sm:w-auto"} h-12 px-8 font-bold uppercase tracking-wide transition-colors disabled:opacity-60 flex items-center justify-center gap-2 ${isFinal ? "bg-[#ff3b30] text-white hover:bg-[#e6352b] animate-pulse" : "bg-[#ccff00] text-black hover:bg-[#b8e600]"}`}
      data-testid={testid}
    >
      {loading ? (
        <span className={`w-4 h-4 border-2 ${isFinal ? "border-white" : "border-black"} border-t-transparent rounded-full animate-spin`} />
      ) : (
        <><Lightning size={16} weight="bold" /> {isFinal ? "LAST CHANCE — 50% OFF ENDS TONIGHT" : "UPGRADE TO PRO — 50% OFF"}</>
      )}
    </button>
  );

  return (
    <div className="min-h-screen bg-[#0a0a0a] pb-20 lg:pb-0">
      <SEO
        title="50% Off Dynasty Trade Maker Pro — Code DYNASTY50"
        description="Ends Friday, October 30: get your first year of Dynasty Trade Maker Pro for 50% off with code DYNASTY50. AI trade packages, Trade Finder, and every Pro tool."
        path="/dynasty50"
      />
      <PublicNav />

      {expired ? (
        <div className="max-w-3xl mx-auto px-4 py-20 text-center" data-testid="promo-expired">
          <h1 className="font-heading text-4xl sm:text-5xl text-white uppercase mb-4">THIS OFFER HAS ENDED</h1>
          <p className="text-[#a3a3a3] mb-6">The DYNASTY50 promotion is over, but Pro is still just $29/year.</p>
          <Link to="/pricing" className="inline-block bg-[#ccff00] text-black font-bold px-8 py-3 no-underline">SEE PRICING</Link>
        </div>
      ) : (
        <>
          {/* 1. HERO — split layout with graphic */}
          <div className="relative overflow-hidden">
            <div className="max-w-6xl mx-auto px-4 pt-10 sm:pt-14 pb-10 grid lg:grid-cols-[1.1fr_1fr] gap-8 lg:gap-12 items-center">
              <div>
                <div className={`inline-flex items-center gap-2 border text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 mb-5 ${isFinal ? "bg-[#ff3b30] border-[#ff3b30] text-white animate-pulse" : "bg-[#ff3b30]/10 border-[#ff3b30]/40 text-[#ff3b30]"}`} data-testid="promo-deadline-badge">
                  <Clock size={13} weight="bold" /> {isFinal ? "LAST 24 HOURS — ENDS TONIGHT AT MIDNIGHT" : `Ends midnight, ${deadline}`}
                </div>
                <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white uppercase tracking-tight mb-4" data-testid="promo-title">
                  HALFWAY TO THE PLAYOFFS.<br /><span className="text-[#ccff00]">MAKE YOUR MOVE.</span>
                </h1>
                <p className="text-base md:text-lg text-[#a3a3a3] mb-3">
                  Dynasty Trade Maker Pro, <span className="text-white font-bold">50% off your first year</span>.
                </p>
                <div className="flex items-end gap-3 mb-6">
                  <span className="text-xl text-[#666] line-through">$29</span>
                  <span className="font-heading text-5xl text-[#ccff00]">$14.50</span>
                  <span className="text-xs text-[#a3a3a3] mb-1.5">first year · that's $1.21/mo</span>
                </div>
                <div className="mb-6"><ClaimButton testid="promo-claim-btn" /></div>
                {promo?.expires_at && <Countdown expiresAt={promo.expires_at} final={isFinal} />}
              </div>
              <div className="relative hidden sm:block">
                <img
                  src="/ad-graphics/fb-ad-square-visual.jpg"
                  alt="Dynasty Trade Maker player value analytics"
                  className="w-full max-w-md mx-auto border border-[#262626]"
                  data-testid="promo-hero-graphic"
                />
                <div className="absolute -top-3 -right-3 bg-[#ff3b30] text-white font-heading text-xl px-4 py-2 rotate-3 shadow-lg">50% OFF</div>
                <div className="absolute -bottom-6 left-0 right-0 max-w-sm mx-auto hidden lg:block">
                  <SampleVerdict />
                </div>
              </div>
            </div>
          </div>

          {/* 2. STATS BAR */}
          <div className="border-y border-[#262626] bg-[#111]" data-testid="promo-stats-bar">
            <div className="max-w-6xl mx-auto px-4 py-6 grid grid-cols-3 gap-4">
              {STATS.map(s => (
                <div key={s.label} className="text-center">
                  <p className="font-heading text-2xl sm:text-4xl text-[#ccff00]">{s.value}</p>
                  <p className="text-[10px] sm:text-xs uppercase tracking-wider text-[#666] mt-1">{s.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Sample verdict shown inline on smaller screens */}
          <div className="max-w-xl mx-auto px-4 pt-10 lg:hidden">
            <SampleVerdict />
          </div>

          {/* 3. THE OUTCOME */}
          <div className="max-w-3xl mx-auto px-4 pt-10 pb-10">
            <div className="bg-[#111] border-l-2 border-[#ccff00] p-5 sm:p-6">
              <p className="text-sm sm:text-base text-[#d4d4d4] leading-relaxed">
                <span className="text-white font-bold">Half the season is gone.</span> The playoff stretch is where championships are decided — and the managers who win are the ones making the right trades right now. Pro is the toolkit to do it: AI trade packages built for your roster, a live read on every contender, and the data to know exactly who to buy and who to sell.
              </p>
            </div>
          </div>

          {/* 4. WHAT YOU GET */}
          <div className="max-w-4xl mx-auto px-4 pb-10">
            <h2 className="text-base md:text-lg font-bold text-white uppercase tracking-wider mb-4 flex items-center gap-2"><Trophy size={18} weight="bold" className="text-[#ccff00]" /> What you get with Pro</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {PRO_FEATURES.map((f) => {
                const Icon = f.icon;
                return (
                  <div key={f.title} className="bg-[#111] border border-[#262626] p-4 hover:border-[#ccff00]/40 transition-colors">
                    <div className="flex items-center gap-2.5 mb-2">
                      <div className="w-8 h-8 bg-[#ccff00]/10 border border-[#ccff00]/30 flex items-center justify-center shrink-0"><Icon size={16} weight="bold" className="text-[#ccff00]" /></div>
                      <p className="text-sm font-bold text-white">{f.title}</p>
                    </div>
                    <p className="text-xs text-[#a3a3a3]">{f.desc}</p>
                  </div>
                );
              })}
            </div>
            <p className="text-center text-xs sm:text-sm text-[#a3a3a3] mt-6 flex items-center justify-center gap-2" data-testid="promo-credibility">
              <ChartBar size={15} weight="bold" className="text-[#ccff00]" />
              Every recommendation is built on <span className="text-white font-bold">167,000 real dynasty trades</span> and <span className="text-white font-bold">10,000 championship rosters</span> — not opinions.
            </p>
          </div>

          {/* 5. PRICING CLARITY */}
          <div className="max-w-md mx-auto px-4 pb-10">
            <div className="relative bg-[#111] border border-[#ccff00]/30 p-6 sm:p-8 text-center">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#ccff00] text-black text-[10px] font-bold uppercase tracking-wider px-3 py-1">You save $14.50</div>
              <div className="flex items-end justify-center gap-3 mb-1 mt-2">
                <span className="text-2xl text-[#666] line-through">$29</span>
                <span className="font-heading text-5xl sm:text-6xl text-[#ccff00]" data-testid="promo-price">$14.50</span>
                <span className="text-sm text-[#a3a3a3] mb-2">first year</span>
              </div>
              <p className="text-[11px] text-[#a3a3a3] mb-5" data-testid="promo-renewal-note">50% off your first year with code DYNASTY50. Renews at $29/year after — cancel anytime.</p>
              <ClaimButton testid="promo-claim-btn-card" full />
              <button
                onClick={copyCode}
                className="w-full mt-3 flex items-center justify-center gap-2 text-xs text-[#a3a3a3] hover:text-white transition-colors border border-dashed border-[#333] py-2.5"
                data-testid="promo-copy-code-btn"
              >
                <Tag size={13} /> Code <span className="font-mono font-bold text-[#ccff00]">DYNASTY50</span> — pre-applied at checkout, or copy it <Copy size={13} />
              </button>
              <div className="flex items-center justify-center gap-4 mt-4 text-[10px] text-[#666]">
                <span className="flex items-center gap-1"><ShieldCheck size={12} className="text-[#4ade80]" /> Secure Stripe checkout</span>
                <span className="flex items-center gap-1"><Check size={12} className="text-[#4ade80]" /> Cancel anytime</span>
              </div>
            </div>
          </div>

          {/* 6. REPEAT CTA + COUNTDOWN */}
          <div className="max-w-3xl mx-auto px-4 pb-12 text-center">
            <p className="text-sm text-[#a3a3a3] mb-4">
              {isFinal ? <span className="text-[#ff3b30] font-bold uppercase">This is it — the offer ends tonight at midnight</span> : <>Offer ends midnight, <span className="text-white font-bold">{deadline}</span></>}{promo?.expires_at && <> — <Countdown expiresAt={promo.expires_at} compact /></>}
            </p>
            <div className="flex justify-center"><ClaimButton testid="promo-claim-btn-bottom" /></div>
          </div>

          {/* 7. FAQ */}
          <div className="max-w-2xl mx-auto px-4 pb-16">
            <h2 className="text-base md:text-lg font-bold text-white uppercase tracking-wider mb-4">Questions</h2>
            {FAQS.map((f, i) => (
              <div key={i} className="border-b border-[#262626]">
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between py-3.5 text-left" data-testid={`promo-faq-${i}`}>
                  <span className="text-sm font-semibold text-white">{f.q}</span>
                  <span className="text-[#ccff00] text-lg leading-none">{openFaq === i ? "−" : "+"}</span>
                </button>
                {openFaq === i && <p className="text-sm text-[#a3a3a3] pb-4">{f.a}</p>}
              </div>
            ))}
          </div>

          {/* 8. STICKY MOBILE CTA */}
          <div className={`fixed bottom-0 left-0 right-0 z-40 bg-[#0a0a0a]/95 backdrop-blur-sm border-t p-3 lg:hidden ${isFinal ? "border-[#ff3b30]" : "border-[#262626]"}`} data-testid="promo-sticky-cta">
            <div className="flex items-center justify-between gap-3 max-w-xl mx-auto">
              <div>
                <p className="text-sm font-bold text-white"><span className="text-[#666] line-through text-xs mr-1">$29</span>$14.50 <span className="text-[10px] text-[#a3a3a3] font-normal">first year</span></p>
                {promo?.expires_at && <p className="text-[10px] text-[#ff3b30]">{isFinal && <span className="font-bold uppercase">Ends tonight · </span>}<Countdown expiresAt={promo.expires_at} compact /> left</p>}
              </div>
              <button onClick={() => handleClaim()} disabled={loading || error} className={`h-10 px-5 font-bold text-xs uppercase tracking-wide flex items-center gap-1.5 disabled:opacity-60 ${isFinal ? "bg-[#ff3b30] text-white animate-pulse" : "bg-[#ccff00] text-black"}`} data-testid="promo-sticky-claim-btn">
                {loading ? <span className={`w-3.5 h-3.5 border-2 ${isFinal ? "border-white" : "border-black"} border-t-transparent rounded-full animate-spin`} /> : <><Lightning size={13} weight="bold" /> {isFinal ? "LAST CHANCE" : "CLAIM 50% OFF"}</>}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
